import { NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { LayoutDashboard, ShieldAlert, AlertTriangle, Gauge, CheckSquare, FileText, Users, ScrollText, LogOut, Settings, User as UserIcon, HelpCircle, Sparkles, Rocket, Cloud, Map as MapIcon } from "lucide-react";
import { APP_VERSION, BUILD_DATE } from "@/version";
import AdvisorWidget from "@/components/AdvisorWidget";
import NotificationsBell from "@/components/NotificationsBell";
import OnboardingWizard from "@/components/OnboardingWizard";

const NAV = [
  { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard, testid: "nav-dashboard" },
  { to: "/risks", label: "Risk Register", icon: ShieldAlert, testid: "nav-risks" },
  { to: "/findings", label: "Findings", icon: AlertTriangle, testid: "nav-findings" },
  { to: "/appetite", label: "Risk Appetite", icon: Gauge, testid: "nav-appetite" },
  { to: "/acceptances", label: "Acceptances", icon: CheckSquare, testid: "nav-acceptances" },
  { to: "/reports", label: "Reports", icon: FileText, testid: "nav-reports" },
  { to: "/projects", label: "Projects", icon: Rocket, testid: "nav-projects" },
  { to: "/assessments", label: "M365 Assessment", icon: Cloud, testid: "nav-assessments" },
  { to: "/advisor", label: "Advisor Notes", icon: Sparkles, testid: "nav-advisor" },
  { to: "/roadmap", label: "Roadmap", icon: MapIcon, testid: "nav-roadmap" },
  { to: "/how-to-use", label: "How to use", icon: HelpCircle, testid: "nav-how-to-use" },
];

const ADMIN_NAV = [
  { to: "/users", label: "Users & Roles", icon: Users, testid: "nav-users" },
  { to: "/audit", label: "Audit Trail", icon: ScrollText, testid: "nav-audit" },
  { to: "/settings", label: "Tenant Settings", icon: Settings, testid: "nav-settings" },
];

/** Convert #rrggbb → {r,g,b} → hsl-like alpha layers. Falls back to defaults on bad input. */
function _accentPalette(hex) {
  const h = /^#([0-9a-f]{6})$/i.exec(hex || "");
  if (!h) return { base: "#0A1329", deep: "#050B1F", hover: "rgba(255,255,255,0.06)", active: "rgba(255,255,255,0.14)" };
  const n = parseInt(h[1], 16);
  const r = (n >> 16) & 0xff, g = (n >> 8) & 0xff, b = n & 0xff;
  // build a deep navy from the accent by mixing with #050B1F
  const mix = (a, b, t) => Math.round(a * (1 - t) + b * t);
  const deep = `rgb(${mix(r, 5, 0.82)}, ${mix(g, 11, 0.82)}, ${mix(b, 31, 0.82)})`;
  const base = `rgb(${mix(r, 10, 0.7)}, ${mix(g, 19, 0.7)}, ${mix(b, 41, 0.7)})`;
  return { base, deep, hover: `rgba(${r},${g},${b},0.14)`, active: `rgba(${r},${g},${b},0.28)` };
}

export default function AppShell({ children }) {
  const { user, tenant, logout } = useAuth();
  const nav = useNavigate();
  const accent = tenant?.primary_color || "#0E7490";
  const pal = _accentPalette(accent);

  // Per-tenant module gates — super admin can turn Assessments on/off per
  // customer. Legacy tenants without a `modules` field are treated as fully
  // enabled by the backend so nothing regresses.
  const assessmentsEnabled = tenant?.modules?.assessments !== false;
  const visibleNav = NAV.filter((item) => !(item.to === "/assessments" && !assessmentsEnabled));

  const linkCls = ({ isActive }) =>
    `group flex items-center gap-3 rounded-lg px-3 py-2 text-sm transition-all ${
      isActive
        ? "text-white shadow-sm"
        : "text-slate-300 hover:text-white"
    }`;
  const linkStyle = ({ isActive }) => ({
    background: isActive ? pal.active : "transparent",
  });

  const initials = (user?.name || user?.email || "?").split(" ").map((p) => p[0]).slice(0, 2).join("").toUpperCase();

  return (
    <div className="app-shell min-h-screen flex bg-[#FBFBF9]" data-testid="app-shell">
      {/* Sidebar */}
      <aside
        className="hidden lg:flex flex-col w-64 text-slate-100 border-r border-slate-800/60"
        style={{ background: `linear-gradient(180deg, ${pal.base} 0%, ${pal.deep} 100%)` }}
      >
        <div className="px-5 py-5 border-b border-white/10">
          <div className="flex items-center gap-2.5">
            {/* Landing-style gradient dot mark */}
            <div className="h-9 w-9 rounded-md bg-gradient-to-br from-rose-500 via-amber-500 to-emerald-500 p-[1.5px]">
              <div className="h-full w-full rounded-[5px] bg-[#050B1F] flex items-center justify-center">
                <div className="h-2 w-2 rounded-full bg-rose-400 shadow-[0_0_10px_rgba(244,63,94,0.7)]" />
              </div>
            </div>
            <div>
              <div className="font-display font-bold text-lg leading-none">RiskEye</div>
              <div className="text-[10px] uppercase tracking-widest text-slate-400 mt-1">a seQure Product</div>
            </div>
          </div>
        </div>
        <div className="px-3 py-4">
          {tenant && (
            <div className="px-3 pb-3 mb-2 border-b border-white/10">
              <div className="text-[10px] uppercase tracking-widest text-slate-400 font-mono">Tenant</div>
              <div className="text-sm font-semibold text-white mt-1 truncate" data-testid="tenant-name">{tenant.name}</div>
            </div>
          )}
          <nav className="flex flex-col gap-0.5">
            {visibleNav.map(({ to, label, icon: Icon, testid }) => (
              <NavLink key={to} to={to} end={to === "/dashboard"} className={linkCls} style={linkStyle} data-testid={testid}
                onMouseEnter={(e) => { if (!e.currentTarget.className.includes("text-white shadow-sm")) e.currentTarget.style.background = pal.hover; }}
                onMouseLeave={(e) => { if (!e.currentTarget.className.includes("text-white shadow-sm")) e.currentTarget.style.background = "transparent"; }}
              >
                <Icon className="h-4 w-4 shrink-0" />
                <span>{label}</span>
              </NavLink>
            ))}
          </nav>
          {user?.role === "Admin" && (
            <div className="mt-6">
              <div className="px-3 pb-2 text-[10px] uppercase tracking-widest text-slate-400 font-mono">Admin</div>
              <nav className="flex flex-col gap-0.5">
                {ADMIN_NAV.map(({ to, label, icon: Icon, testid }) => (
                  <NavLink key={to} to={to} className={linkCls} style={linkStyle} data-testid={testid}
                    onMouseEnter={(e) => { if (!e.currentTarget.className.includes("text-white shadow-sm")) e.currentTarget.style.background = pal.hover; }}
                    onMouseLeave={(e) => { if (!e.currentTarget.className.includes("text-white shadow-sm")) e.currentTarget.style.background = "transparent"; }}
                  >
                    <Icon className="h-4 w-4 shrink-0" />
                    <span>{label}</span>
                  </NavLink>
                ))}
                {user?.is_super_admin && (
                  <>
                    <NavLink to="/portfolio" className={linkCls} style={linkStyle} data-testid="nav-portfolio">
                      <LayoutDashboard className="h-4 w-4 shrink-0" />
                      <span>Portfolio</span>
                      <span className="ml-auto text-[9px] font-mono uppercase text-amber-300 border border-amber-400/50 px-1 rounded">super</span>
                    </NavLink>
                    <NavLink to="/tenants" className={linkCls} style={linkStyle} data-testid="nav-tenants">
                      <Users className="h-4 w-4 shrink-0" />
                      <span>All Tenants</span>
                    </NavLink>
                  </>
                )}
              </nav>
            </div>
          )}
        </div>
        <div className="mt-auto p-4 border-t border-white/10">
          <NavLink to="/profile" className="flex items-center gap-2.5 rounded-lg p-2 hover:bg-white/[0.06] transition-colors group" data-testid="nav-profile">
            <div className="h-8 w-8 rounded-full bg-slate-700 flex items-center justify-center text-white font-semibold text-[11px] overflow-hidden shrink-0">
              {user?.avatar_data ? <img src={user.avatar_data} alt="" className="h-full w-full object-cover" /> : initials}
            </div>
            <div className="min-w-0 flex-1">
              <div className="text-xs text-white font-medium truncate">{user?.name || user?.email}</div>
              <div className="text-[10px] text-slate-400 font-mono truncate">{user?.role}</div>
            </div>
            <UserIcon className="h-3.5 w-3.5 text-slate-400 group-hover:text-white transition-colors shrink-0" />
          </NavLink>
          <Button size="sm" variant="ghost" className="w-full mt-1.5 text-slate-300 hover:text-white hover:bg-white/[0.08] justify-start" onClick={async () => { await logout(); nav("/login"); }} data-testid="logout-btn">
            <LogOut className="h-3.5 w-3.5 mr-1.5" /> Sign out
          </Button>
          <div className="mt-3 flex items-center justify-between text-[9.5px] font-mono uppercase tracking-[0.18em] text-slate-500" data-testid="app-version">
            <NavLink to="/changelog" className="hover:text-white transition-colors" title={`Build ${BUILD_DATE} — release notes`} target="_blank">v{APP_VERSION}</NavLink>
            <span className="text-slate-600">prod</span>
          </div>
        </div>
      </aside>

      {/* Main */}
      <div className="flex-1 flex flex-col min-w-0">
        <header className="h-14 border-b border-slate-200 bg-white/95 backdrop-blur-md sticky top-0 z-30 flex items-center px-6 justify-between">
          <div className="lg:hidden font-display font-bold text-slate-900">RiskEye</div>
          <div className="hidden lg:flex items-center gap-3 text-xs text-slate-500 font-mono">
            <span className="uppercase tracking-widest">a seQure Product</span>
          </div>
          <div className="flex items-center gap-3">
            <OnboardingWizard variant="pill" />
            <NotificationsBell />
            <AdvisorWidget />
            <div className="hidden sm:block h-6 w-px bg-slate-200" />
            <span className="hidden sm:inline text-xs text-slate-500">Signed in as</span>
            <NavLink to="/profile" className="text-sm font-medium text-slate-800 hover:text-slate-950" data-testid="header-profile-link">
              {user?.name || user?.email}
            </NavLink>
          </div>        </header>
        <main className="flex-1 overflow-y-auto">{children}</main>
      </div>
    </div>
  );
}
